import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { parseTokens, serializeTokens } from "./tokenText";
import MetricPicker from "./MetricPicker";
import "./NarrativeTokenField.css";

/**
 * NarrativeTokenField - the `@`-mention editor for a narrative PROSE field (SP6 Task 4, reworked).
 *
 * The value/onChange contract is UNCHANGED and always the plain token string (`{{fig:KEY}}` tokens
 * inline in prose) - the exact string persistence + renderNarrative already use; a token-free value
 * round-trips byte-identical (see tokenText.js round-trip identity).
 *
 * WHY A SINGLE contentEditable (not per-run `<input>`s): a narrative field is multi-sentence prose
 * with a token possibly embedded MID-paragraph. A plain `<input>` cannot wrap, so the previous
 * per-text-run model rendered any long field as a row of fixed-width scrolling boxes and could not
 * flow as a paragraph. Here the whole field is ONE `contentEditable` div whose children are normal
 * inline flow - text nodes for text segments and, for each fig segment, an atomic chip span
 * (`contentEditable={false}`, so the browser treats it as a single non-caret-traversable unit).
 * Text therefore WRAPS naturally and chips sit inline exactly where the token is.
 *
 * CONTROLLED-ish sync: the editable div carries NO React children; its DOM is managed imperatively
 * (the standard controlled-contentEditable escape hatch, so React never clobbers the caret on a
 * keystroke). `renderSegmentsInto` builds children from `value` on mount and whenever an INCOMING
 * `value` differs from the last string we emitted (`lastEmittedRef`); our own edits are skipped so
 * typing never resets the caret. `onInput` walks the DOM back to segments (`domToSegments`) and
 * emits `serializeTokens(...)`.
 *
 * TESTABILITY (jsdom 16 has no real caret/selection - Range.toString across nodes returns "" and
 * addRange is a no-op): all caret/mention/insert/delete LOGIC lives in the pure, exported,
 * selection-free helpers below (`domToSegments`, `matchTrailingMention`, `spliceChipIntoTextNode`,
 * `deleteChipNode`) and is unit-tested directly on constructed DOM nodes. The component reads the
 * live caret where a real browser provides one and otherwise falls back to "caret at end of the
 * field" - which is precisely the case when an author is typing at the end, and is what the
 * component-level tests drive.
 */

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

// A trailing `@` followed by zero-or-more token-key chars (the SAME charset tokenText/renderNarrative
// accept), anchored to end-of-string. `@` alone yields query "" (picker lists everything).
const TRAILING_MENTION = /@([a-z0-9_]*)$/i;

/**
 * matchTrailingMention(textBeforeCaret) -> query string | null (PURE).
 * Returns the mention query (chars after `@`, possibly "") when the text immediately before the
 * caret ends in an `@mention`, else null. This is the whole `@`-detection rule, extracted so it's
 * unit-testable without a DOM or a caret.
 */
export function matchTrailingMention(textBeforeCaret) {
  if (typeof textBeforeCaret !== "string") return null;
  const m = TRAILING_MENTION.exec(textBeforeCaret);
  return m ? m[1] : null;
}

/**
 * domToSegments(container) -> segments[] (PURE): walk a contentEditable container's direct child
 * nodes into the same segment shape parseTokens produces, so `serializeTokens(domToSegments(c))`
 * yields the plain token string. Text node -> text segment; chip span (has `data-fig-key`) -> fig
 * segment; a stray `<br>` (browsers insert one on Enter) -> a "\n" text segment; any other stray
 * element -> its textContent as text. Selection-free, so it's directly unit-testable.
 */
export function domToSegments(container) {
  const segments = [];
  if (!container) return segments;
  container.childNodes.forEach((node) => {
    if (node.nodeType === TEXT_NODE) {
      segments.push({ type: "text", value: node.textContent });
    } else if (node.nodeType === ELEMENT_NODE) {
      const figKey = node.dataset ? node.dataset.figKey : node.getAttribute("data-fig-key");
      if (figKey) {
        segments.push({ type: "fig", key: figKey });
      } else if (node.tagName === "BR") {
        segments.push({ type: "text", value: "\n" });
      } else {
        segments.push({ type: "text", value: node.textContent });
      }
    }
  });
  return segments;
}

/** The live display text for a fig chip: the metric's live value, else its label, else an em-dash. */
function chipDisplay(key, metrics) {
  const metric = metrics && metrics[key];
  return (metric && (metric.value ?? metric.label)) ?? "—";
}

/** Build a fresh atomic chip DOM node for `key`. `contentEditable=false` makes it atomic. */
function makeChipNode(key, metrics) {
  const span = document.createElement("span");
  span.className = "ntf-chip";
  span.setAttribute("contenteditable", "false");
  span.setAttribute("data-fig-key", key);
  span.textContent = chipDisplay(key, metrics);
  return span;
}

/** Imperatively (re)build the container's children from a segment list. Clears first. */
function renderSegmentsInto(container, segments, metrics) {
  if (!container) return;
  container.textContent = "";
  segments.forEach((seg) => {
    if (seg.type === "fig") {
      container.appendChild(makeChipNode(seg.key, metrics));
    } else if (seg.value) {
      container.appendChild(document.createTextNode(seg.value));
    }
  });
}

/**
 * spliceChipIntoTextNode(textNode, caretOffset, chip) -> boolean (PURE DOM, no selection).
 * If the text of `textNode` up to `caretOffset` ends in an `@mention`, remove that `@mention` and
 * splice `chip` in at that spot, leaving the trailing text (if any) after the chip; returns true.
 * Returns false (no mutation) when there's no trailing mention. This is the core insert operation,
 * unit-tested directly by passing a constructed text node + explicit offset - covering the
 * mid-paragraph splice a real caret would drive.
 */
export function spliceChipIntoTextNode(textNode, caretOffset, chip) {
  if (!textNode || textNode.nodeType !== TEXT_NODE) return false;
  const data = textNode.data;
  const upToCaret = data.slice(0, caretOffset);
  const m = TRAILING_MENTION.exec(upToCaret);
  if (!m) return false;
  const at = caretOffset - m[0].length;
  const beforeText = data.slice(0, at);
  const afterText = data.slice(caretOffset);
  const parent = textNode.parentNode;
  if (!parent) return false;
  textNode.data = beforeText;
  parent.insertBefore(chip, textNode.nextSibling);
  if (afterText) parent.insertBefore(document.createTextNode(afterText), chip.nextSibling);
  // Drop a now-empty leading text node so the DOM stays clean (and domToSegments emits no "" run).
  if (beforeText === "") parent.removeChild(textNode);
  return true;
}

/** Fallback insert when there's no live caret: splice into the LAST text node, caret assumed at
 *  its end (the typing-at-the-end case). Returns true on success. */
function spliceChipAtEnd(container, chip) {
  const nodes = container.childNodes;
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (n.nodeType === TEXT_NODE) {
      return spliceChipIntoTextNode(n, n.data.length, chip);
    }
    if (n.nodeType === ELEMENT_NODE) {
      // Last child is already a chip/element - nothing to strip; append the new chip after it.
      container.appendChild(chip);
      return true;
    }
  }
  container.appendChild(chip);
  return true;
}

/**
 * deleteChipNode(chip) (PURE DOM): remove a chip and merge the text nodes that flanked it, so
 * "before" + "after" become one run (no stray empty run between two text segments). Unit-tested
 * directly; the component wires it to a real Backspace-before-chip caret in a browser.
 */
export function deleteChipNode(chip) {
  if (!chip) return;
  const parent = chip.parentNode;
  if (!parent) return;
  const prev = chip.previousSibling;
  const next = chip.nextSibling;
  parent.removeChild(chip);
  if (prev && next && prev.nodeType === TEXT_NODE && next.nodeType === TEXT_NODE) {
    prev.data += next.data;
    parent.removeChild(next);
  }
}

/** Read the field text up to the caret. Uses the live Selection where the browser provides one
 *  (real caret); falls back to the full text (caret-at-end) when selection is unavailable/empty -
 *  which is the jsdom case and the typing-at-end case. */
function getTextBeforeCaret(container) {
  const sel = typeof window !== "undefined" && window.getSelection && window.getSelection();
  if (sel && sel.rangeCount > 0 && sel.focusNode && container.contains(sel.focusNode)) {
    try {
      const r = document.createRange();
      r.selectNodeContents(container);
      r.setEnd(sel.focusNode, sel.focusOffset);
      const s = r.toString();
      if (s) return s;
    } catch (e) {
      /* fall through to the caret-at-end fallback */
    }
  }
  return container.textContent || "";
}

/** Place the caret immediately after `node` (best-effort; a no-op where selection is unsupported). */
function placeCaretAfter(node) {
  try {
    const sel = window.getSelection && window.getSelection();
    if (!sel) return;
    const r = document.createRange();
    r.setStartAfter(node);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  } catch (e) {
    /* selection unsupported (jsdom) - caret restoration is a real-browser concern only */
  }
}

const NarrativeTokenField = ({
  value,
  onChange,
  metrics,
  catalog,
  disabled,
  placeholder,
  ariaLabel,
  onFocus,
}) => {
  const containerRef = useRef(null);
  // The last token string WE emitted - lets the sync effect tell an author's own edit (skip, keep
  // caret) from an externally-changed `value` (rebuild the DOM).
  const lastEmittedRef = useRef(null);
  // Latest metrics for imperative chip building outside React's render (e.g. on pick/insert).
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;

  // mentionQuery: null = picker closed; a string (possibly "") = picker open with this query.
  const [mentionQuery, setMentionQuery] = useState(null);
  const pickerRef = useRef(null);

  // Controlled-ish sync: rebuild children only when the INCOMING value isn't the one we just
  // emitted (external change / initial mount); otherwise just refresh chip display values in place
  // (e.g. metrics resolved) without touching text nodes, so the caret is preserved while typing.
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    if (value !== lastEmittedRef.current) {
      renderSegmentsInto(c, parseTokens(value), metrics);
      lastEmittedRef.current = value;
    } else {
      c.querySelectorAll(".ntf-chip").forEach((chip) => {
        chip.textContent = chipDisplay(chip.dataset.figKey, metrics);
      });
    }
  }, [value, metrics]);

  const emit = () => {
    const c = containerRef.current;
    if (!c) return;
    const str = serializeTokens(domToSegments(c));
    lastEmittedRef.current = str;
    if (onChange) onChange(str);
  };

  const closePicker = () => setMentionQuery(null);

  const refreshMention = () => {
    if (disabled) return;
    const c = containerRef.current;
    if (!c) return;
    const query = matchTrailingMention(getTextBeforeCaret(c));
    setMentionQuery(query);
  };

  const handleInput = () => {
    if (disabled) return;
    emit();
    refreshMention();
  };

  const handlePick = (key) => {
    const c = containerRef.current;
    if (!c || disabled) return;
    const chip = makeChipNode(key, metricsRef.current);

    let done = false;
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.rangeCount > 0 && sel.focusNode && sel.focusNode.nodeType === TEXT_NODE && c.contains(sel.focusNode)) {
      done = spliceChipIntoTextNode(sel.focusNode, sel.focusOffset, chip);
    }
    if (!done) done = spliceChipAtEnd(c, chip);
    if (!done) c.appendChild(chip);

    placeCaretAfter(chip);
    closePicker();
    emit();
    if (c.focus) c.focus(); // restore focus to the field after inserting (the focus-bug fix)
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    // While the picker is open the caret lives in THIS field, so the field must forward the
    // navigation keys to the picker (the Critical fix) - typing `@` then ArrowDown+Enter with no
    // mouse must insert the highlighted metric.
    if (mentionQuery !== null) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        e.preventDefault();
        if (pickerRef.current) pickerRef.current.handleKey(e.key); // Enter -> onPick -> handlePick
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closePicker();
        return;
      }
    }

    // Atomic chip delete: Backspace with the caret at the very start of a text run whose previous
    // sibling is a chip removes the whole chip. (contentEditable=false spans delete atomically in a
    // real browser anyway; this makes the before-chip case deterministic. Logic is covered by the
    // pure deleteChipNode unit test - jsdom has no caret to drive it here.)
    if (e.key === "Backspace") {
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed && sel.focusOffset === 0) {
        const node = sel.focusNode;
        const prev = node ? node.previousSibling : null;
        const chip =
          prev && prev.nodeType === ELEMENT_NODE && prev.dataset && prev.dataset.figKey ? prev : null;
        if (chip) {
          e.preventDefault();
          deleteChipNode(chip);
          closePicker();
          emit();
        }
      }
    }
  };

  const pickerOpen = !disabled && mentionQuery !== null;

  return (
    <div className="narrative-token-field-wrap">
      <div
        ref={containerRef}
        className={"narrative-token-field" + (disabled ? " is-disabled" : "")}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        data-placeholder={placeholder || ""}
        tabIndex={disabled ? -1 : 0}
        onInput={handleInput}
        onKeyUp={refreshMention}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
      />
      <MetricPicker
        ref={pickerRef}
        open={pickerOpen}
        query={mentionQuery ?? ""}
        catalog={catalog}
        metrics={metrics}
        onPick={handlePick}
        onClose={closePicker}
      />
    </div>
  );
};

NarrativeTokenField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  metrics: PropTypes.object,
  catalog: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      group: PropTypes.string,
    })
  ),
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  onFocus: PropTypes.func,
};

export default NarrativeTokenField;
